import type {
  TelegramSettingsUpdate,
  TelegramSettingsView,
  TelegramTestResult,
} from "@screener/protocol";
import { useEffect, useRef, useSyncExternalStore } from "react";
import type { ApiResult } from "./api-result";
import { clearTelegram, getTelegram, testTelegram, updateTelegram } from "./telegram-api";
import {
  draftFromView,
  type TelegramDraft,
  telegramPatch,
  validateTelegramDraft,
} from "./telegram-draft";

/**
 * Контроллер блока Telegram §6.6 (design.md решение 8) — тем же приёмом, что
 * `use-alert-actions.ts` (`createAlertController`): чистый объект с
 * внедрёнными зависимостями, тестируемый без рендера, и тонкий хук над ним
 * на `useSyncExternalStore`.
 */

export interface TelegramSettingsDeps {
  getTelegram: () => Promise<ApiResult<TelegramSettingsView>>;
  updateTelegram: (
    patch: Partial<TelegramSettingsUpdate>,
  ) => Promise<ApiResult<TelegramSettingsView>>;
  clearTelegram: () => Promise<ApiResult<TelegramSettingsView>>;
  testTelegram: () => Promise<ApiResult<TelegramTestResult>>;
  onSessionInvalid: () => void;
  /** `bot_token_set && chat_id !== null` — зовётся после каждого полученного вида. */
  onConfiguredChange: (configured: boolean) => void;
}

export type TelegramTestState =
  | { kind: "idle" }
  | { kind: "testing" }
  | { kind: "result"; result: TelegramTestResult }
  /** Демон не ответил на проверку — отдельно от отказа Telegram (`rejected`/`unavailable`). */
  | { kind: "daemon_unavailable" };

export type TelegramSaveError = "unavailable" | "invalid";

export interface TelegramReadySnapshot {
  status: "ready";
  view: TelegramSettingsView;
  draft: TelegramDraft;
  /** Поле токена открыто — либо токен не задан, либо нажато «Заменить». */
  replacingToken: boolean;
  /** Ошибка черновика до отправки — `null`, если сохранять можно. */
  fieldError: string | null;
  /** `fieldError` пуст, `patch` не пуст, ни сохранение, ни отключение не идёт. */
  canSave: boolean;
  /** Черновик отличается от сохранённого — `patch` не пуст либо введён токен (design.md решение 8, ревью). */
  draftDirty: boolean;
  saving: boolean;
  /** «сохранено» — до следующей правки поля. */
  savedNotice: boolean;
  saveError: TelegramSaveError | null;
  testState: TelegramTestState;
  confirmingDisconnect: boolean;
  disconnecting: boolean;
  /** Отключение не выполнилось — демон недоступен, подтверждение снято. */
  disconnectError: boolean;
}

export type TelegramSettingsSnapshot =
  | { status: "loading" }
  | { status: "unavailable" }
  | TelegramReadySnapshot;

export interface TelegramSettingsController {
  /** Читает настройки один раз — зовётся на монтировании. */
  start(): void;
  /** Перечитывает настройки из `unavailable`. */
  retry(): void;
  setToken(value: string): void;
  /** Открывает поле токена у уже заданного (design: «Заменить»). */
  startReplace(): void;
  /** Закрывает поле токена, очищая введённое (design: «Отменить замену»). */
  cancelReplace(): void;
  setChatId(value: string): void;
  setInterval(value: string): void;
  save(): void;
  test(): void;
  requestDisconnect(): void;
  cancelDisconnect(): void;
  confirmDisconnect(): void;
  /**
   * Окно закрыто: введённое стирается, замена токена и подтверждение
   * отключения закрываются. Сохранённого не меняет — запросов не шлёт.
   */
  discardDraft(): void;
  subscribe(listener: () => void): () => void;
  getSnapshot(): TelegramSettingsSnapshot;
}

interface ReadyInternalState {
  status: "ready";
  view: TelegramSettingsView;
  draft: TelegramDraft;
  replacingToken: boolean;
  saving: boolean;
  savedNotice: boolean;
  saveError: TelegramSaveError | null;
  testState: TelegramTestState;
  confirmingDisconnect: boolean;
  disconnecting: boolean;
  disconnectError: boolean;
}

type InternalState = { status: "loading" } | { status: "unavailable" } | ReadyInternalState;

export function isConfigured(view: TelegramSettingsView): boolean {
  return view.bot_token_set && view.chat_id !== null;
}

export function createTelegramSettingsController(
  deps: TelegramSettingsDeps,
): TelegramSettingsController {
  let state: InternalState = { status: "loading" };
  const listeners = new Set<() => void>();
  let cachedSnapshot: TelegramSettingsSnapshot | undefined;
  let dirty = true;
  // Счётчик попыток проверки Telegram (ревью, второй круг, пункт 1) — растёт
  // на каждый применённый вид (`applyView`: чтение, сохранение, отключение) и
  // на старте новой проверки. Ответ `test()` применяется, только если его
  // номер (`attempt`) всё ещё совпадает со счётчиком к моменту ответа —
  // иначе за время проверки состояние Telegram сменилось (сохранением или
  // отключением), и ответ уже не про него.
  let testAttempt = 0;
  // Номер проверки, чей ответ ещё не пришёл, или `null` — держит кнопку
  // «Проверить» недоступной и не даёт `applyView` сбросить `testState` в
  // `idle`, пока эта проверка не ответит (даже устаревшим ответом).
  let pendingTestAttempt: number | null = null;

  function notify(): void {
    dirty = true;
    for (const listener of listeners) {
      listener();
    }
  }

  function readyState(
    view: TelegramSettingsView,
    opts: { savedNotice: boolean },
  ): ReadyInternalState {
    return {
      status: "ready",
      view,
      draft: draftFromView(view),
      replacingToken: false,
      saving: false,
      savedNotice: opts.savedNotice,
      saveError: null,
      // Результат прежней проверки относился к прежнему состоянию Telegram — после
      // чтения, сохранения и отключения он устарел (design.md решение 8, ревью).
      // Но если проверка ещё не ответила (`pendingTestAttempt`), состояние
      // остаётся «идёт проверка»: иначе кнопка «Проверить» ненадолго
      // разблокировалась бы, пока первая проверка всё ещё в пути (ревью,
      // второй круг, пункт 1) — сброс в `idle` придёт вместе с её ответом.
      testState: pendingTestAttempt !== null ? { kind: "testing" } : { kind: "idle" },
      confirmingDisconnect: false,
      disconnecting: false,
      disconnectError: false,
    };
  }

  /** Применяет прочитанный/записанный вид: сбрасывает черновик и извещает вызывающего (design.md решение 8). */
  function applyView(view: TelegramSettingsView, opts: { savedNotice: boolean }): void {
    // Любой применённый вид делает номер уже начатой проверки устаревшим —
    // её ответ, когда придёт, не должен показаться пользователю как проверка
    // текущего состояния (ревью, второй круг, пункт 1).
    testAttempt += 1;
    state = readyState(view, opts);
    notify();
    deps.onConfiguredChange(isConfigured(view));
  }

  function load(): void {
    state = { status: "loading" };
    notify();
    deps
      .getTelegram()
      .then((result) => {
        if (result.status === "ok") {
          applyView(result.value, { savedNotice: false });
          return;
        }
        if (result.status === "unauthorized") {
          deps.onSessionInvalid();
          return;
        }
        state = { status: "unavailable" };
        notify();
      })
      .catch((error: unknown) => {
        console.error("не удалось прочитать настройки telegram", error);
        state = { status: "unavailable" };
        notify();
      });
  }

  function start(): void {
    load();
  }

  function retry(): void {
    load();
  }

  function updateReady(patch: Partial<ReadyInternalState>): void {
    if (state.status !== "ready") {
      return;
    }
    state = { ...state, ...patch };
    notify();
  }

  function setToken(value: string): void {
    if (state.status !== "ready") {
      return;
    }
    updateReady({ draft: { ...state.draft, token: value }, savedNotice: false });
  }

  function startReplace(): void {
    if (state.status !== "ready") {
      return;
    }
    updateReady({ replacingToken: true, savedNotice: false });
  }

  function cancelReplace(): void {
    if (state.status !== "ready") {
      return;
    }
    updateReady({
      replacingToken: false,
      draft: { ...state.draft, token: "" },
      savedNotice: false,
    });
  }

  function setChatId(value: string): void {
    if (state.status !== "ready") {
      return;
    }
    updateReady({ draft: { ...state.draft, chatId: value }, savedNotice: false });
  }

  function setInterval(value: string): void {
    if (state.status !== "ready") {
      return;
    }
    updateReady({ draft: { ...state.draft, interval: value }, savedNotice: false });
  }

  function save(): void {
    if (state.status !== "ready" || state.saving || state.disconnecting) {
      return;
    }
    const fieldError = validateTelegramDraft(state.view, state.draft);
    if (fieldError !== null) {
      return;
    }
    const patch = telegramPatch(state.view, state.draft);
    if (Object.keys(patch).length === 0) {
      return;
    }
    updateReady({ saving: true, savedNotice: false, saveError: null });
    deps
      .updateTelegram(patch)
      .then((result) => {
        if (state.status !== "ready") {
          return;
        }
        if (result.status === "ok") {
          applyView(result.value, { savedNotice: true });
          return;
        }
        if (result.status === "unauthorized") {
          deps.onSessionInvalid();
          return;
        }
        const kind: TelegramSaveError = result.status === "invalid" ? "invalid" : "unavailable";
        updateReady({ saving: false, saveError: kind });
      })
      .catch((error: unknown) => {
        console.error("не удалось записать настройки telegram", error);
        updateReady({ saving: false, saveError: "unavailable" });
      });
  }

  function test(): void {
    // Недоступна, пока идёт другая проверка, сохранение или отключение
    // (ревью, второй круг, пункт 1) — сохранение и отключение во время уже
    // идущей проверки, наоборот, разрешены: они делают её ответ устаревшим,
    // а не блокируются им.
    if (
      state.status !== "ready" ||
      state.saving ||
      state.disconnecting ||
      pendingTestAttempt !== null
    ) {
      return;
    }
    testAttempt += 1;
    const attempt = testAttempt;
    pendingTestAttempt = attempt;
    updateReady({ testState: { kind: "testing" } });
    deps
      .testTelegram()
      .then((result) => {
        const isCurrent = attempt === testAttempt;
        pendingTestAttempt = null;
        if (state.status !== "ready") {
          return;
        }
        if (!isCurrent) {
          // Ответ устарел: пока проверка была в пути, состояние Telegram
          // сменилось сохранением или отключением (`applyView`) — результат
          // не про него, показываем не его, а просто гасим «идёт проверку».
          if (state.testState.kind === "testing") {
            updateReady({ testState: { kind: "idle" } });
          }
          return;
        }
        if (result.status === "ok") {
          updateReady({ testState: { kind: "result", result: result.value } });
          return;
        }
        if (result.status === "unauthorized") {
          deps.onSessionInvalid();
          return;
        }
        updateReady({ testState: { kind: "daemon_unavailable" } });
      })
      .catch((error: unknown) => {
        const isCurrent = attempt === testAttempt;
        pendingTestAttempt = null;
        console.error("не удалось выполнить проверку telegram", error);
        if (state.status !== "ready") {
          return;
        }
        if (!isCurrent) {
          if (state.testState.kind === "testing") {
            updateReady({ testState: { kind: "idle" } });
          }
          return;
        }
        updateReady({ testState: { kind: "daemon_unavailable" } });
      });
  }

  function requestDisconnect(): void {
    if (state.status !== "ready" || state.saving) {
      return;
    }
    updateReady({ confirmingDisconnect: true });
  }

  function cancelDisconnect(): void {
    if (state.status !== "ready") {
      return;
    }
    updateReady({ confirmingDisconnect: false });
  }

  function confirmDisconnect(): void {
    if (state.status !== "ready" || state.disconnecting || state.saving) {
      return;
    }
    updateReady({ disconnecting: true, disconnectError: false });
    deps
      .clearTelegram()
      .then((result) => {
        if (state.status !== "ready") {
          return;
        }
        if (result.status === "ok") {
          applyView(result.value, { savedNotice: false });
          return;
        }
        if (result.status === "unauthorized") {
          deps.onSessionInvalid();
          return;
        }
        updateReady({ disconnecting: false, disconnectError: true, confirmingDisconnect: false });
      })
      .catch((error: unknown) => {
        console.error("не удалось отключить telegram", error);
        updateReady({ disconnecting: false, disconnectError: true, confirmingDisconnect: false });
      });
  }

  function discardDraft(): void {
    if (state.status !== "ready") {
      return;
    }
    // Контроллер живёт в каркасе дольше окна: без сброса набранный токен
    // остался бы в памяти и вернулся бы при следующем открытии.
    updateReady({
      draft: draftFromView(state.view),
      replacingToken: false,
      confirmingDisconnect: false,
      savedNotice: false,
      saveError: null,
    });
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function getSnapshot(): TelegramSettingsSnapshot {
    if (!dirty && cachedSnapshot) {
      return cachedSnapshot;
    }
    if (state.status !== "ready") {
      cachedSnapshot = state;
    } else {
      const fieldError = validateTelegramDraft(state.view, state.draft);
      const patch = telegramPatch(state.view, state.draft);
      const draftDirty = Object.keys(patch).length > 0 || state.draft.token !== "";
      cachedSnapshot = {
        status: "ready",
        view: state.view,
        draft: state.draft,
        replacingToken: state.replacingToken,
        fieldError,
        canSave:
          !state.saving &&
          !state.disconnecting &&
          fieldError === null &&
          Object.keys(patch).length > 0,
        draftDirty,
        saving: state.saving,
        savedNotice: state.savedNotice,
        saveError: state.saveError,
        testState: state.testState,
        confirmingDisconnect: state.confirmingDisconnect,
        disconnecting: state.disconnecting,
        disconnectError: state.disconnectError,
      };
    }
    dirty = false;
    return cachedSnapshot;
  }

  return {
    start,
    retry,
    setToken,
    startReplace,
    cancelReplace,
    setChatId,
    setInterval,
    save,
    test,
    requestDisconnect,
    cancelDisconnect,
    confirmDisconnect,
    discardDraft,
    subscribe,
    getSnapshot,
  };
}

export interface UseTelegramSettingsOptions {
  onSessionInvalid: () => void;
  onConfiguredChange: (configured: boolean) => void;
}

/**
 * Реальные обёртки запросного канала (`telegram-api.ts`) заводятся один раз
 * на монтирование — контроллер держится в `useRef`, вся логика решения в нём.
 */
export function useTelegramSettings({
  onSessionInvalid,
  onConfiguredChange,
}: UseTelegramSettingsOptions): {
  snapshot: TelegramSettingsSnapshot;
  controller: TelegramSettingsController;
} {
  const onSessionInvalidRef = useRef(onSessionInvalid);
  const onConfiguredChangeRef = useRef(onConfiguredChange);
  useEffect(() => {
    onSessionInvalidRef.current = onSessionInvalid;
  }, [onSessionInvalid]);
  useEffect(() => {
    onConfiguredChangeRef.current = onConfiguredChange;
  }, [onConfiguredChange]);

  const controllerRef = useRef<TelegramSettingsController | undefined>(undefined);
  if (!controllerRef.current) {
    controllerRef.current = createTelegramSettingsController({
      getTelegram,
      updateTelegram,
      clearTelegram,
      testTelegram,
      onSessionInvalid: () => onSessionInvalidRef.current(),
      onConfiguredChange: (configured) => onConfiguredChangeRef.current(configured),
    });
  }
  const controller = controllerRef.current;

  useEffect(() => {
    // Читаем ровно один раз на монтирование — повторный монтаж StrictMode
    // перечитывает те же настройки, что безвредно (design.md решение 8:
    // чтение идемпотентно, в отличие от заявок на звук `use-alert-actions`).
    controller.start();
  }, [controller]);

  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  return { snapshot, controller };
}
